import React from 'react';
import SnackbarContent from '@mui/material/SnackbarContent';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import * as css from './localAlert.module.scss';
import classNames from 'classnames';
import Button from '../button';
import alpColors from '../../alpColors';

export enum AlertType {
  DEFAULT = 'default',
  WHITE = 'white',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
}

const getVariantIcon = (type: AlertType | undefined) => {
  switch (type) {
    case AlertType.DEFAULT:
    case AlertType.WHITE:
    case AlertType.ERROR:
      return ErrorOutlineIcon;
    case AlertType.INFO:
      return InfoIcon;
    case AlertType.WARNING:
      return WarningIcon;
    default:
      return ErrorOutlineIcon;
  }
};

const getIconStyle = (type: AlertType | undefined) => {
  switch (type) {
    case AlertType.DEFAULT:
      return css.defaultIconColor;
    case AlertType.WHITE:
      return css.whiteIconColor;
    case AlertType.ERROR:
      return css.errorIconColor;
    case AlertType.INFO:
      return css.infoIconColor;
    case AlertType.WARNING:
      return css.warningIconColor;
    default:
      return css.defaultIconColor;
  }
};

const getSnackbarContentStyle = (type: AlertType | undefined): string => {
  switch (type) {
    case AlertType.DEFAULT:
      return css.defaultStyle;
    case AlertType.WHITE:
      return css.white;
    case AlertType.ERROR:
      return css.error;
    case AlertType.INFO:
      return css.info;
    case AlertType.WARNING:
      return css.warning;
    default:
      return css.defaultStyle;
  }
};

export interface LocalAlertProps {
  type: AlertType;
  contents: React.ReactNode;
  handleClose: () => void;
  open: boolean;
}

export type PropsHidden = Partial<LocalAlertProps>;

type Props = LocalAlertProps | PropsHidden;

const LocalAlert: React.FC<React.PropsWithChildren<Props>> = ({
  open,
  type,
  contents,
  handleClose,
}) => {
  if (!open) return <></>;

  const snackbarContentStyle = getSnackbarContentStyle(type);
  const Icon = getVariantIcon(type);
  const iconStyle = getIconStyle(type);

  return (
    <SnackbarContent
      className={snackbarContentStyle}
      sx={{
        minWidth: '500',
        margin: '5px 0',
        boxShadow: 'none',
        color: alpColors.martinique,
        border: `solid 1px ${alpColors.black10}`,
      }}
      message={
        <span className={css.message}>
          <Icon className={classNames(css.icon, iconStyle)} />
          {contents}
        </span>
      }
      action={<Button.Close onClick={handleClose} />}
    />
  );
};

export default LocalAlert;
